import ViewAllProfilesIcon from '@assets/images/icons-filled/view-all-profiles.svg';
import {
  FeatureFlagTypes,
  OrganizationUserAccessTypes,
} from '@generated/graphql';
import type { IUserEntity } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog.types';
import {
  type ActiveEntityProps,
  UserContext,
} from '@providers/userContextProvider';
import { entityName } from '@utils/common';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { Divider, ListSubheader, MenuItem } from 'hephaestus/web';
import { AddCircleOutlined as AddCircleOutlineIcon } from 'hephaestus/webIcons';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOrganizationFeatureFlag } from '../../../hooks/useOrganizationFeatureFlag';
import { EntityListItem } from './EntityListItem';

const MAX_PROFILES_IN_MENU = 4;

const profileSortName = (entity?: ActiveEntityProps) =>
  entityName(entity) || entity?.primaryEmail || '';

export type EntityListProp = {
  handleClose: () => void;
  onOpenViewAllProfiles: () => void;
  onOpenCreateEntity: () => void;
};

export const EntityList = ({
  handleClose,
  onOpenViewAllProfiles,
  onOpenCreateEntity,
}: EntityListProp) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['entityMenu', 'navMenu', 'shared']);

  const allowAddingNewProfile = useOrganizationFeatureFlag(
    FeatureFlagTypes.AllowAddingANewProfile,
  );
  const { userContext, activeEntity, setActiveEntity, setActiveHousehold } =
    useContext(UserContext);

  const entities: IUserEntity[] =
    userContext.accessType === OrganizationUserAccessTypes.RepCode
      ? (userContext?.repCodeEntities ?? [])
      : (userContext?.entities ?? []);
  const nonAffiliateEntities = entities.filter((x) => !x.entity.affiliateOnly);

  const getGroupedEntitiesSortedByType = (type?: string) => {
    const isIndividual = type?.toUpperCase() === 'INDIVIDUAL';

    const filtered = nonAffiliateEntities.filter((item) =>
      isIndividual
        ? item.entity.type === 'INDIVIDUAL'
        : item.entity.type !== 'INDIVIDUAL',
    );

    const sorted = [...filtered].sort((a, b) =>
      profileSortName(a.entity).localeCompare(profileSortName(b.entity)),
    );

    const active = sorted.filter((item) => item.entity.id === activeEntity?.id);
    const nonActive = sorted.filter(
      (item) => item.entity.id !== activeEntity?.id,
    );

    return [...active, ...nonActive];
  };

  const handleEntitySelect = (item: IUserEntity) => {
    handleClose();
    const households = filterHouseholdsWithAccess(
      item.entity?.households || [],
      item.entity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView && households.length > 0;
    if (isHouseholdDefaultView) {
      const household = households[0];
      setActiveHousehold(household);
      navigate(`/households/${household?.id}/clients/${item.entity.id}`);
    }
    setActiveEntity(item.entity);
  };

  const individualEntities = getGroupedEntitiesSortedByType('individual');
  const nonIndividualEntities = getGroupedEntitiesSortedByType();

  const activeEntityItem = nonAffiliateEntities.find(
    (en) => en.entity.id === activeEntity?.id,
  );
  const totalEntitiesCount =
    individualEntities.length + nonIndividualEntities.length;

  return (
    <>
      {totalEntitiesCount <= MAX_PROFILES_IN_MENU ? (
        <>
          {individualEntities.map((en, index) => (
            <EntityListItem
              item={en}
              isActive={en.entity.id === activeEntity?.id}
              index={index}
              onClick={() => handleEntitySelect(en)}
              key={en.entity.id}
            />
          ))}
          {individualEntities.length > 0 &&
            nonIndividualEntities.length > 0 && <Divider />}
          {nonIndividualEntities.length > 0 && (
            <ListSubheader>
              {t('navMenu:menu.nonIndividualClients').toUpperCase()}
            </ListSubheader>
          )}
          {nonIndividualEntities.map((en, index) => (
            <EntityListItem
              item={en}
              isActive={en.entity.id === activeEntity?.id}
              index={index}
              onClick={() => handleEntitySelect(en)}
              key={en.entity.id}
            />
          ))}
        </>
      ) : (
        <>
          {activeEntityItem && (
            <EntityListItem
              item={activeEntityItem}
              isActive
              index={0}
              onClick={() => handleEntitySelect(activeEntityItem)}
            />
          )}
          <MenuItem
            data-testid='view-all-profile'
            onClick={() => {
              handleClose();
              onOpenViewAllProfiles();
            }}
          >
            <img src={ViewAllProfilesIcon} alt='' height='32px' width='32px' />
            {t('viewAllProfiles')}
          </MenuItem>
        </>
      )}
      <Divider />
      {allowAddingNewProfile && (
        <MenuItem
          data-testid='add-new-entity'
          onClick={() => {
            handleClose();
            onOpenCreateEntity();
          }}
        >
          <AddCircleOutlineIcon fontSize='inherit' />
          {t('addNewEntity')}
        </MenuItem>
      )}
    </>
  );
};
